import React, { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle, Circle, BookOpen, ChevronRight, X, Play, Lock } from 'lucide-react'
import './ModulePanel.css'

export function ModulePanel({
  isOpen, onClose,
  eyebrow, title, color,
  sections,
  resumeLesson,
  totalLessons, completedCount,
  credentialId, onCertClick,
  // ── New props ──
  isSubActive,        // boolean — true when subscription_status === 'active'
  onLockedLessonClick, // (lessonTitle) => void — show paywall modal
}) {
  const navigate = useNavigate()
  const [openModules, setOpenModules] = useState({})

  const toggleModule = useCallback((key) => {
    setOpenModules(prev => ({ ...prev, [key]: !prev[key] }))
  }, [])

  const handleLessonClick = useCallback((slug, lessonTitle) => {
    // Block everything for unpaid users — no free preview, no ref lessons,
    // no intros. One rule: no subscription = paywall.
    if (!isSubActive) {
      onLockedLessonClick?.(lessonTitle)
      return
    }
    onClose()
    navigate(`/learn/${slug}`)
  }, [isSubActive, onLockedLessonClick, onClose, navigate])

  const handleResumeClick = useCallback(() => {
    if (!resumeLesson) return
    handleLessonClick(resumeLesson.slug, resumeLesson.title)
  }, [resumeLesson, handleLessonClick])

  const pct = totalLessons > 0
    ? Math.round((completedCount / totalLessons) * 100) : 0

  const badgeClass   = { accent:'panel-badge--it', teal:'panel-badge--hs', indigo:'panel-badge--ai', security:'panel-badge--branch' }[color] || 'panel-badge--it'
  const fillClass    = { accent:'prog-fill--it',   teal:'prog-fill--hs',   indigo:'prog-fill--ai',   security:'prog-fill--branch'   }[color] || 'prog-fill--it'
  const resumeClass  = { accent:'panel-resume--it', teal:'panel-resume--hs', indigo:'panel-resume--ai', security:'panel-resume--branch' }[color] || 'panel-resume--it'

  return (
    <>
      <div className={`panel-overlay ${isOpen ? 'panel-overlay--open' : ''}`} onClick={onClose} />
      <aside className={`module-panel ${isOpen ? 'module-panel--open' : ''}`}>

        <div className="module-panel__header">
          <div className="module-panel__top">
            <span className={`panel-badge ${badgeClass}`}>{eyebrow}</span>
            <button className="panel-close-btn" onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>
          <div className="module-panel__title">{title}</div>
          <div className="module-panel__meta mono">{totalLessons} lessons</div>
          <div className="module-panel__prog">
            <div className="module-panel__prog-bar">
              <div className={`module-panel__prog-fill ${fillClass}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="module-panel__prog-label mono">{completedCount} / {totalLessons}</span>
          </div>
          {credentialId ? (
            <button className="cert-strip" onClick={onCertClick}>
              <div>
                <div className="cert-strip__label">Foundation Ready — Certificate</div>
                <div className="cert-strip__id mono">{credentialId}</div>
              </div>
              <ChevronRight size={13} />
            </button>
          ) : resumeLesson ? (
            <button className={`panel-resume ${resumeClass}`} onClick={handleResumeClick}>
              <Play size={12} />
              <span>Resume — {resumeLesson.title}</span>
            </button>
          ) : null}
        </div>

        <div className="module-panel__body">
          {sections.map((section, si) => (
            <div key={si}>
              {section.heading && (
                <div className="module-panel__section-head mono">{section.heading}</div>
              )}
              {section.modules.map((mod, mi) => {
                const key = `${si}_${mi}`
                const isModOpen = openModules[key] !== undefined
                  ? openModules[key]
                  : (mi === 0 && !credentialId)
                const doneLessons = mod.lessons.filter(l => l.done && !l.isRef).length
                const mainCount   = mod.lessons.filter(l => !l.isRef).length

                return (
                  <div key={key} className="mod-accordion">
                    <button className="mod-accordion__trigger" onClick={() => toggleModule(key)}>
                      <span className="mod-accordion__num mono">{mod.num}</span>
                      <div className="mod-accordion__info">
                        <span className="mod-accordion__title">{mod.title}</span>
                        <span className="mod-accordion__count mono">{mod.lessons.length} lessons</span>
                      </div>
                      <span className="mod-accordion__done mono">{doneLessons}/{mainCount}</span>
                      <ChevronRight size={13} className={`mod-accordion__chev ${isModOpen ? 'mod-accordion__chev--open' : ''}`} />
                    </button>
                    {isModOpen && (
                      <div className="mod-lesson-list">
                        {mod.lessons.map(lesson => (
                          <button
                            key={lesson.id}
                            className={`mod-lesson-row ${lesson.done ? 'mod-lesson-row--done' : ''} ${lesson.isCurrent ? 'mod-lesson-row--current' : ''} ${!isSubActive ? 'mod-lesson-row--locked' : ''}`}
                            onClick={() => handleLessonClick(lesson.slug, lesson.title)}
                          >
                            <span className="mod-lesson-row__icon">
                              {!isSubActive
                                ? <Lock size={13} className="icon-locked" />
                                : lesson.isRef
                                ? <BookOpen size={14} className="icon-ref" />
                                : lesson.done
                                ? <CheckCircle size={14} className="icon-done" />
                                : <Circle size={14} className="icon-todo" />}
                            </span>
                            <span className="mod-lesson-row__title">{lesson.title}</span>
                            {lesson.isRef && <span className="mod-lesson-row__tag mod-lesson-row__tag--ref mono">Ref</span>}
                            {lesson.isCurrent && isSubActive && <span className={`mod-lesson-row__tag mod-lesson-row__tag--${color} mono`}>Current</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </aside>
    </>
  )
}
